import { omit } from 'lodash';

import { AccessesToCustomerTreePayload } from './ducks';
import {
  AccessWithCpRefNo,
  DeleteExternalUserAccesses,
  HigherIdentificationAccessRequest,
  HigherIdentificationRequest,
  HigherIdentificationUpdateRequest,
  SkipVerificationExternalUserAccessesRequest,
  VerifyExternalUserAccessesRequest,
} from 'generated/bffUserAccess/models';
import { apiClient, ExtendedAxiosResponse } from 'helpers/apiClient';
import { ChannelType } from 'models/ChannelType';
import { GetExternalUserAccessFilter } from 'models/GetExternalUserAccessFilter';
import { Pagination } from 'models/Pagination';
import { CustomerMasterSourceType } from 'models/swagger/customerMasterSourceType';

export const api = {
  getAccessesToEntity: (
    url: string,
    contextCuRefNo: string,
    contextEntityRefNo: string,
    contextEntityTypeId: string,
    searchString: string
  ): Promise<ExtendedAxiosResponse> =>
    apiClient.get(`${url}/accesses-to-entity`, {
      params: {
        channel: ChannelType.MO2_CARE,
        customerMasterSource: CustomerMasterSourceType.O2POP,
        forceCheck: true,
        page: 1,
        pageSize: 20,
        contextEntityTypeId: contextEntityTypeId,
        contextEntityRefNo: contextEntityRefNo,
        contextCuRefNo: contextCuRefNo,
        searchString: searchString,
      },
    }),
  getAccessesToCustomerTree: (
    url: string,
    cuRefNo: string,
    pagination?: Pagination,
    cpRefNo?: string,
    filter?: AccessesToCustomerTreePayload
  ): Promise<ExtendedAxiosResponse> => {
    return apiClient.get(`${url}/accesses-to-customer-tree`, {
      params: {
        channel: ChannelType.MO2_CARE,
        customerMasterSource: CustomerMasterSourceType.O2POP,
        cuRefNo: cuRefNo,
        cpRefNo: cpRefNo,
        searchString: filter?.searchString,
        roleType: filter?.roleType,
        ...omit(
          {
            ...pagination,
            forceCheck: true,
          },
          ['initPages']
        ),
      },
    });
  },
  getExternalUserAccesses: (
    url: string,
    contactPersonRefNo: string,
    contextCuRefNo: string
  ): Promise<ExtendedAxiosResponse> => {
    return apiClient.get(`${url}/external-user-accesses`, {
      params: {
        forceCheck: true,
        contactPersonRefNo: contactPersonRefNo,
        contextCuRefNo: contextCuRefNo,
        customerMasterSource: CustomerMasterSourceType.O2POP,
        channel: ChannelType.MO2_CARE,
      },
      headers: {
        'X-O2-System-Consumer': 'MO2-CARE',
      },
    });
  },
  deleteExternalUserAccesses: (url: string, data: DeleteExternalUserAccesses): Promise<ExtendedAxiosResponse> => {
    return apiClient.delete(`${url}/external-user-accesses`, {
      data: data,
      params: {
        customerMasterSource: CustomerMasterSourceType.O2POP,
        channel: ChannelType.MO2_CARE,
      },
    });
  },
  putVerifyExternalUserAccesses: (
    url: string,
    data: VerifyExternalUserAccessesRequest
  ): Promise<ExtendedAxiosResponse> => {
    return apiClient.put(`${url}/verify-external-user-accesses`, data, {
      params: {
        customerMasterSource: CustomerMasterSourceType.O2POP,
        channel: ChannelType.MO2_CARE,
      },
    });
  },
  putSkipVerificationExternalUserAccesses: (
    url: string,
    data: Partial<SkipVerificationExternalUserAccessesRequest>
  ): Promise<ExtendedAxiosResponse> => {
    return apiClient.put(`${url}/skip-verification-external-user-accesses`, data, {
      params: {
        customerMasterSource: CustomerMasterSourceType.O2POP,
        channel: ChannelType.MO2_CARE,
      },
    });
  },
  getExternalUserAccessFilter: (
    url: string,
    filter: GetExternalUserAccessFilter,
    cuRefNo?: string
  ): Promise<ExtendedAxiosResponse> => {
    return apiClient.get(`${url}/external-user-access-filter`, {
      params: {
        customerMasterSource: CustomerMasterSourceType.O2POP,
        channel: ChannelType.MO2_CARE,
        contactPersonEmail: filter?.contactPersonEmail,
        contactPersonRefNo: filter?.contactPersonRefNo,
        cuRefNo: cuRefNo,
        entityIdentifier: filter?.entityIdentifier,
        entityRefNo: filter?.entityRefNo,
        entityTypeId: filter?.entityTypeId,
        forceCheck: filter?.forceCheck,
      },
    });
  },
  createExternalUserAccess: (url: string, data: any): Promise<ExtendedAxiosResponse> => {
    return apiClient.post(`${url}/external-user-access`, data, {
      params: {
        customerMasterSource: CustomerMasterSourceType.O2POP,
        channel: ChannelType.MO2_CARE,
      },
    });
  },
  updateExternalUserAccess: (url: string, data: AccessWithCpRefNo): Promise<ExtendedAxiosResponse> => {
    return apiClient.put(`${url}/external-user-access`, data, {
      params: {
        customerMasterSource: CustomerMasterSourceType.O2POP,
        channel: ChannelType.MO2_CARE,
        entityType: data.accessToEntityRef.entityType,
      },
    });
  },
  getAttemptsToVerifyContactPerson: (
    url: string,
    cuRefNo: string,
    cpRefNo?: string
  ): Promise<ExtendedAxiosResponse> => {
    return apiClient.get(`${url}/attemptsToVerifyContactPerson`, {
      params: {
        customerMasterSource: CustomerMasterSourceType.O2POP,
        channel: ChannelType.MO2_CARE,
        cuRefNo: cuRefNo,
        cpRefNo: cpRefNo,
      },
    });
  },
  createHigherIdentification: (url: string, data: HigherIdentificationRequest): Promise<ExtendedAxiosResponse> => {
    return apiClient.post(`${url}/external-user-accesses/higher-identifications`, data, {
      params: {
        customerMasterSource: CustomerMasterSourceType.O2POP,
        channel: ChannelType.MO2_CARE,
      },
    });
  },
  updateHigherIdentification: (
    url: string,
    data: HigherIdentificationUpdateRequest
  ): Promise<ExtendedAxiosResponse> => {
    return apiClient.patch(`${url}/external-user-accesses/higher-identifications`, data, {
      params: {
        customerMasterSource: CustomerMasterSourceType.O2POP,
        channel: ChannelType.MO2_CARE,
      },
    });
  },
  deleteHigherIdentification: (
    url: string,
    data: HigherIdentificationAccessRequest
  ): Promise<ExtendedAxiosResponse> => {
    return apiClient.delete(`${url}/external-user-accesses/higher-identifications`, {
      data: data,
      params: {
        customerMasterSource: CustomerMasterSourceType.O2POP,
        channel: ChannelType.MO2_CARE,
      },
    });
  },
  getAccessesToCustomerTreeToFile: (
    url: string,
    cuRefNo: string,
    cpRefNo?: string,
    filter?: AccessesToCustomerTreePayload
  ): Promise<ExtendedAxiosResponse> => {
    return apiClient.get(`${url}/accesses-to-customer-tree-to-file`, {
      params: {
        channel: ChannelType.MO2_CARE,
        customerMasterSource: CustomerMasterSourceType.O2POP,
        cuRefNo: cuRefNo,
        cpRefNo: cpRefNo,
        searchString: filter?.searchString,
        roleType: filter?.roleType,
        ...omit(
          {
            forceCheck: false,
          },
          ['initPages']
        ),
      },
    });
  },
};
